import { useState } from "react";
import { Button } from "@safers/ui/atoms";
import { useAppSetup } from "@/hooks/useAppSetup";
import { notificationView } from "@/lib/setupView";

export function NotificationBanner() {
  const { notification, env, dismissed, dismiss } = useAppSetup();
  const [guideOpen, setGuideOpen] = useState(false);

  const view = notificationView(notification, env);

  if (dismissed || view.key === "on") return null;

  /*
    거부된 뒤에는 앱이 권한을 다시 물을 수 없다 — `requestPermission()` 이 프롬프트 없이
    즉시 denied 로 끝난다. 그래서 이 상태의 액션만 권한 요청이 아니라 "어디를 눌러라" 를
    펴고 접는 것이다. 하단 고정 바라 평소엔 접어 둔다.
  */
  const guideToggle = view.guide &&
    view.key === "denied" && {
      body: guideOpen ? view.guide : view.body,
      action: { label: guideOpen ? "접기" : "허용 방법", onClick: () => setGuideOpen((v) => !v) },
    };

  // 안내문이 있으면 그게 본문이다 — iOS `홈 화면에 추가` 는 절차가 곧 내용이라, 예전
  // 설치 배너도 `installGuideText` 를 본문으로 썼다. 차단 상태만 토글 뒤로 접는다.
  const body = guideToggle ? guideToggle.body : (view.guide ?? view.body);
  const action = guideToggle ? guideToggle.action : (view.action ?? null);

  return (
    <div
      role="dialog"
      aria-label="알림 설정 안내"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-neutral-200 bg-white px-4 py-3 shadow-lg dark:border-neutral-700 dark:bg-neutral-900"
    >
      {/*
        배너 전체가 눌린다. 손가락이 가는 곳은 작은 버튼이 아니라 이 덩어리다.
        버튼에서 올라온 클릭은 되돌려보낸다 — 안 그러면 두 번 실행된다.
      */}
      <div
        onClick={
          action
            ? (e) => {
                if ((e.target as HTMLElement).closest("button")) return;
                action.onClick();
              }
            : undefined
        }
        className={`mx-auto flex max-w-screen-sm items-center gap-3${action ? " cursor-pointer" : ""}`}
      >
        <img
          src={`${import.meta.env.BASE_URL}icons/icon-192.png`}
          alt=""
          className="h-10 w-10 shrink-0 rounded-lg"
        />

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">
            {view.title}
          </p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">{body}</p>
        </div>

        {action && (
          <Button size="sm" onClick={action.onClick}>
            {action.label}
          </Button>
        )}

        <button
          type="button"
          onClick={dismiss}
          aria-label="닫기"
          className="shrink-0 rounded-md p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
          data-env={`${env.os}/${env.browser}`}
        >
          ✕
        </button>
      </div>
    </div>
  );
}
